// src/utils/csvParser.js

import { hasMeaningfulValue, looksLikeTimestampValue, toNumber } from './helpers';
import { HEADER_PATTERNS, CABLING_HEADERS } from '../constants';

export const cleanStr = (str) => {
  return str
    .replace(/^\uFEFF/, '')
    .replace(/\u00A0/g, ' ')
    .replace(/[\u200B\u200C\u200D\uFEFF]/g, '')
    .replace(/\r\n/g, '\n')
    .replace(/\r/g, '\n');
};

export const cleanCell = (raw) => {
  if (!raw) return '';
  return raw
    .split('\n')
    .map(line => line.trim())
    .join('\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
};

export const parseCSV = (csvText, { headerRow = true } = {}) => {
  const text = cleanStr(csvText);
  const rows = [];
  let row = [], cell = '', inQuotes = false;

  for (let i = 0; i < text.length; i++) {
    const char = text[i];
    const nextChar = text[i + 1];

    if (char === '"') {
      if (inQuotes && nextChar === '"') {
        cell += '"';
        i++;
      } else {
        inQuotes = !inQuotes;
      }
    } else if (char === ',' && !inQuotes) {
      row.push(cleanCell(cell));
      cell = '';
    } else if (char === '\n' && !inQuotes) {
      row.push(cleanCell(cell));
      cell = '';
      if (row.length > 0) rows.push(row);
      row = [];
    } else if (char === '\n' && inQuotes) {
      cell += '\n';
    } else {
      cell += char;
    }
  }

  if (cell || row.length) {
    row.push(cleanCell(cell));
    if (row.length > 0) rows.push(row);
  }

  if (rows.length === 0) return rows;

  // Normalize row lengths
  const headerLength = rows[0].length;
  const normalized = rows.map(row => {
    const out = [...row];
    while (out.length < headerLength) out.push('');
    if (out.length > headerLength) out.splice(headerLength);
    return out;
  });

  if (!headerRow) {
    return normalized.filter(row => row.some(cell => cell !== ''));
  }

  const header = normalized[0];
  const dataRows = normalized.slice(1).filter(row =>
    row.some(cell => cell !== '')
  );

  return [header, ...dataRows];
};

export const isTimestampSupportRow = (row, headers) => {
  const nonEmptyCells = row.filter((cell) => hasMeaningfulValue(cell));

  // Completely empty rows can be ignored.
  if (nonEmptyCells.length === 0) {
    return true;
  }

  /*
   * A real inventory/data row must never be removed just because it also
   * contains a "Last Updated By" value.
   *
   * This protects:
   * - Name
   * - Description
   * - Product ID
   * - Asset No
   * - Serial No
   *
   * It also keeps the existing cabling protections.
   */
  const hasImportantData = row.some((cell, index) => {
    const header = String(headers[index] || '').trim();

    const normalizedHeader = header
      .toLowerCase()
      .replace(/[-_]/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();

    const value = String(cell || '').trim();

    if (!hasMeaningfulValue(value)) {
      return false;
    }

    // Existing cabling data protections.
    const isBundleData =
      /bundle/i.test(header) &&
      value.length > 3;

    const isRackData =
      /rack/i.test(header) &&
      value.length > 0;

    const isCountData =
      /count|qty/i.test(header) &&
      /^\d+$/.test(value) &&
      Number(value) > 0;

    /*
     * Inventory identity fields.
     *
     * Audit rows may contain timestamps under Asset No or Serial No,
     * so timestamp-looking values are not accepted as identity data.
     */
    const isInventoryIdentityData =
      /^(name|item name|description|item description|product id|product code|asset no|asset number|asset id|serial no|serial number)$/.test(
        normalizedHeader
      ) &&
      !looksLikeTimestampValue(value);

    return (
      isBundleData ||
      isRackData ||
      isCountData ||
      isInventoryIdentityData
    );
  });

  // A row containing actual inventory/cabling identity data is a real row.
  if (hasImportantData) {
    return false;
  }

  // Check remaining rows for timestamp/audit values.
  const timestampCellCount = nonEmptyCells.filter(
    looksLikeTimestampValue
  ).length;

  const timestampHeaderCount = row.filter((cell, index) => {
    const header = String(headers[index] || '');

    return (
      hasMeaningfulValue(cell) &&
      HEADER_PATTERNS.timestamp.test(header)
    );
  }).length;

  /*
   * It is an audit/support row when:
   * - More than half of its populated cells look like timestamps, or
   * - It contains a value under an actual timestamp header.
   */
  return (
    timestampCellCount > nonEmptyCells.length / 2 ||
    timestampHeaderCount > 0
  );
};

export const buildInventoryAssetRows = (rows, headers) => {
  const sheetRows = rows.slice(1);
  const assetRows = [];

  sheetRows.forEach((assetRow, rowIndex) => {
    if (isTimestampSupportRow(assetRow, headers)) return;

    const nextRow = sheetRows[rowIndex + 1] || [];
    const nextRowIsTimestamp = isTimestampSupportRow(nextRow, headers);
    const hasAssetData = assetRow.some(cell => hasMeaningfulValue(cell));

    if (!hasAssetData) return;

    const obj = {};
    headers.forEach((header, colIndex) => {
      const key = String(header || '').trim();
      if (!key) return;

      const assetValue = assetRow[colIndex] || '';
      const timestampValue = nextRowIsTimestamp ? nextRow[colIndex] || '' : '';

      obj[key] = assetValue;

      if (!hasMeaningfulValue(assetValue) &&
        HEADER_PATTERNS.timestamp.test(key) &&
        hasMeaningfulValue(timestampValue)) {
        obj[key] = timestampValue;
      }
    });

    assetRows.push(obj);
  });

  return assetRows;
};

export const mapCsvRowsToObjects = (rows, headers) => {
  return rows
    .slice(1)
    .filter(row => !isTimestampSupportRow(row, headers))
    .map(row => {
      const obj = {};
      headers.forEach((header, index) => {
        obj[header] = row[index] || '';
      });
      return obj;
    });
};

/** Merge alternating timestamp sub-rows into DOA asset rows (keeps all columns). */
export const buildDoaAssetRows = (rows, headers) => {
  const sheetRows = rows.slice(1);
  const assetRows = [];

  sheetRows.forEach((assetRow, rowIndex) => {
    if (isTimestampSupportRow(assetRow, headers)) return;

    const nextRow = sheetRows[rowIndex + 1] || [];
    const nextRowIsTimestamp = isTimestampSupportRow(nextRow, headers);

    const hasIdentity = assetRow.some((cell, index) => {
      const header = String(headers[index] || '');
      return hasMeaningfulValue(cell) && HEADER_PATTERNS.importantData.test(header);
    });

    if (!hasIdentity) return;

    const obj = {};
    headers.forEach((header, colIndex) => {
      const key = String(header || '').trim();
      if (!key) return;

      const assetValue = assetRow[colIndex] || '';
      const siblingValue = nextRowIsTimestamp ? nextRow[colIndex] || '' : '';

      obj[key] = hasMeaningfulValue(assetValue)
        ? assetValue
        : hasMeaningfulValue(siblingValue)
          ? siblingValue
          : assetValue;
    });

    assetRows.push(obj);
  });

  return assetRows;
};

export const getSheetHeaders = (rows) => {
  const raw = rows?.[0] || [];
  return raw.map((header, index) => {
    const trimmed = String(header || '').trim();
    return trimmed || `Column ${index + 1}`;
  });
};

export const parseCablingSheetRows = (rows) => {
  if (!rows || rows.length === 0) return [];

  const headers = rows[0] || [];

  const itemNoIndex = headers.findIndex(h => CABLING_HEADERS.itemNo.test(String(h || '')));
  const productCodeIndex = headers.findIndex(h => CABLING_HEADERS.productCode.test(String(h || '')));
  const dressedInIndex = headers.findIndex(h => CABLING_HEADERS.dressedIn.test(String(h || '')));
  const qtyIndex = headers.findIndex(h => CABLING_HEADERS.qty.test(String(h || '').trim()));
  const roughedInIndexes = headers
    .map((h, idx) => CABLING_HEADERS.roughedIn.test(String(h || '')) ? idx : -1)
    .filter(idx => idx !== -1);

  return rows
    .slice(1)
    .filter(row => !isTimestampSupportRow(row, headers))
    .filter(row => productCodeIndex >= 0 && String(row[productCodeIndex] || '').trim())
    .map((row, index) => {
      const qty = qtyIndex >= 0 ? toNumber(row[qtyIndex]) : 0;
      const roughedIn = roughedInIndexes.reduce((sum, colIdx) => sum + toNumber(row[colIdx]), 0);
      const remaining = Math.max(qty - roughedIn, 0);
      const progress = qty > 0 ? Math.min(100, Math.round((roughedIn / qty) * 100)) : 0;
      const status = getCablingStatus(qty, roughedIn);

      return {
        id: index + 1,
        itemNo: itemNoIndex >= 0 ? row[itemNoIndex] || index + 1 : index + 1,
        productCode: row[productCodeIndex] || '—',
        dressedIn: dressedInIndex >= 0 ? row[dressedInIndex] || '—' : '—',
        qty,
        roughedIn,
        remaining,
        progress,
        status,
      };
    });
};

export const parseFibreCablingSheetRows = (rows) => {
  if (!rows || rows.length === 0) return [];

  const headers = rows[0] || [];

  const findFirstIndex = (patterns) => {
    return headers.findIndex((header) => {
      const h = String(header || '').trim();
      return patterns.some((pattern) => pattern.test(h));
    });
  };

  const getFirstIndex = (patterns) => {
    for (let i = 0; i < headers.length; i++) {
      const h = String(headers[i] || '').trim();
      if (patterns.some((pattern) => pattern.test(h))) {
        return i;
      }
    }
    return -1;
  };

  const getNumericValue = (value) => {
    if (value === undefined || value === null || value === '') return 0;
    const num = Number(String(value).trim());
    if (!isNaN(num) && num > 0) return num;
    const match = String(value).match(/\d+/);
    return match ? parseInt(match[0], 10) : 0;
  };

  const bundleIndex = findFirstIndex([/^bundle$/i, /bundle/i]);
  const rackAIndex = findFirstIndex([/^rack\s*a$/i, /rack\s*a/i]);
  const rackBIndex = findFirstIndex([/^rack\s*b$/i, /rack\s*b/i]);
  const countIndex = findFirstIndex([/^count$/i, /^qty$/i, /quantity/i]);

  const roughedInIndex = getFirstIndex([/roughed\s*in/i]);
  const dressedInIndex = getFirstIndex([/dressed\s*in/i]);
  const testedIndex = getFirstIndex([/tested/i]);
  const terminatedIndex = getFirstIndex([/terminated/i]);

  console.log('Indices:', { bundleIndex, rackAIndex, rackBIndex, countIndex, roughedInIndex });

  // Process data rows (skip header row)
  const dataRows = rows.slice(1);
  console.log('Total data rows (after header):', dataRows.length);

  const parsedRows = [];

  for (let i = 0; i < dataRows.length; i++) {
    const row = dataRows[i];

    console.log(`\n--- Processing row ${i} ---`);
    console.log('Raw row data:', row);

    // Skip empty rows
    const hasData = row.some(cell => cell && String(cell).trim() !== '');
    console.log('Has data:', hasData);
    if (!hasData) {
      console.log('Skipping: empty row');
      continue;
    }

    // Skip timestamp support rows
    const isTimestamp = isTimestampSupportRow(row, headers);
    console.log('Is timestamp row:', isTimestamp);
    if (isTimestamp) {
      console.log('Skipping: timestamp row');
      continue;
    }

    const bundle = bundleIndex >= 0 ? String(row[bundleIndex] || '').trim() : '';
    const rackA = rackAIndex >= 0 ? String(row[rackAIndex] || '').trim() : '';
    const rackB = rackBIndex >= 0 ? String(row[rackBIndex] || '').trim() : '';

    console.log('Bundle:', bundle, 'Rack A:', rackA, 'Rack B:', rackB);

    // Skip if no bundle or rack info
    if (!bundle && !rackA && !rackB) {
      console.log('Skipping: no bundle or rack info');
      continue;
    }

    const qty = countIndex >= 0 ? getNumericValue(row[countIndex]) : 0;
    console.log('Qty:', qty, 'Raw value:', countIndex >= 0 ? row[countIndex] : 'N/A');

    // Skip if qty is 0
    if (qty === 0) {
      console.log('Skipping: qty is 0');
      continue;
    }

    const roughedIn = roughedInIndex >= 0 ? getNumericValue(row[roughedInIndex]) : 0;
    const dressedIn = dressedInIndex >= 0 ? getNumericValue(row[dressedInIndex]) : 0;
    const tested = testedIndex >= 0 ? getNumericValue(row[testedIndex]) : 0;
    const terminated = terminatedIndex >= 0 ? getNumericValue(row[terminatedIndex]) : 0;

    console.log('Progress values - Roughed:', roughedIn, 'Dressed:', dressedIn, 'Tested:', tested, 'Terminated:', terminated);

    const completed = Math.max(roughedIn, dressedIn, tested, terminated);
    const remaining = Math.max(qty - completed, 0);
    const progress = qty > 0 ? Math.min(100, Math.round((completed / qty) * 100)) : 0;

    let status = 'Not Started';
    if (progress >= 100) status = 'Completed';
    else if (progress > 0) status = 'In Progress';

    const connectionPath = [rackA, rackB].filter(Boolean).join(' → ');

    const parsedRow = {
      id: i + 1,
      itemNo: bundle || `Bundle ${i + 1}`,
      productCode: connectionPath || bundle || `Fibre Cable ${i + 1}`,
      bundle: bundle,
      rackA: rackA,
      rackB: rackB,
      qty: qty,
      count: qty,
      roughedIn: roughedIn,
      dressedIn: dressedIn,
      tested: tested,
      terminated: terminated,
      completed: completed,
      remaining: remaining,
      progress: progress,
      status: status,
    };

    console.log('SUCCESS - Adding row:', parsedRow);
    parsedRows.push(parsedRow);
  }

  console.log('Total parsed fibre rows:', parsedRows.length);
  return parsedRows;
};

const getCablingStatus = (qty, roughedIn) => {
  if (qty <= 0) return 'No Qty';
  if (roughedIn <= 0) return 'Not Started';
  if (roughedIn >= qty) return 'Completed';
  return 'In Progress';
};